import { useRouter } from "next/router"
import { type FC, type ReactNode, useContext, useEffect } from "react"

import {
  type BaseCommunityFragment,
  type Community,
  type User,
  useGetMeLazyQuery,
  useLoginMutation,
} from "../generated/graphql"
import { UserContext } from "../pages/UserProvider"

interface UserOnlyProps {
  children: ReactNode | ReactNode[]
}

const UserOnly: FC<UserOnlyProps> = ({ children }) => {
  const { accessToken, setCommunities, setUser, user, setAccessToken, isHydrated } =
    useContext(UserContext)
  const router = useRouter()
  const [login] = useLoginMutation()
  const autologin = (router.query.autologin as string) === "true"

  const [getMe] = useGetMeLazyQuery({
    context: {
      session: {
        accessToken,
      },
    },
  })

  useEffect(() => {
    if (autologin) {
      void login({
        variables: {
          identifier: "superadmin",
          password: "backdoor",
        },
      }).then((res) => {
        if (res.data?.login?.token && res.data?.login?.user) {
          setAccessToken(res.data?.login?.token)
          setUser(res.data?.login?.user)
          setCommunities(res.data?.login?.communities as BaseCommunityFragment[])
        }
      })
    }
  }, [autologin])

  const goToAuth = () => {
    if (router.isReady && !autologin) {
      void router.push(`/user/login?redirectFrom=${router.asPath}`)
    }
  }

  useEffect(() => {
    // Wait for hydration before checking auth
    if (!isHydrated) return

    if (!accessToken) {
      goToAuth()
      return
    }
    void getMe().then(({ data }) => {
      if (data?.user) {
        setCommunities(data?.user?.communitiesWhereAdmin as Community[])
        setUser(data?.user as User)
      } else {
        goToAuth()
      }
    })
  }, [accessToken, router.isReady, isHydrated])

  // Show nothing during hydration to avoid mismatch
  if (!isHydrated) {
    return null
  }

  if (!user) {
    // Still loading or will redirect
    return null
  }

  return <div>{children}</div>
}

export default UserOnly
